<script lang="ts">
  /**
   * THE RUNG PILLS — the owner's flick control, sub-minute included (R565(1), R578(3)①).
   *
   * ⛔ IT RENDERS ONLY WHAT THE HUB HAS SEEN ANSWER. `options` comes from `rungOptions(served)`,
   * and `served` is derived at runtime by `rungFeed.servedRungs()` from the ROUTES' OWN ENVELOPES —
   * not from a constant. An empty list paints NOTHING rather than a control advertising rungs the
   * route may ignore. ⇒ ***A CONTROL IS ONLY AS HONEST AS THE WORST RUNG IT CAN REACH*** (HUDCHAT-v,
   * and it holds one axis wider now that the ladder spans 5s to 1d).
   *
   * ⚠ DELIBERATELY NO SPECIAL STYLING FOR SUB-MINUTE. The temptation is to mark 5s/15s/30s as a
   * separate group — different tint, a divider, a second row. R566(1) is owner word: the stack must
   * read as ONE INSTRUMENT, not four pages stacked. ⇒ ***THE TWO-AXIS SPLIT IS A TYPE-SYSTEM FACT,
   * NOT A VISUAL ONE.*** It exists so `Number("5s")` is unrepresentable in code; the owner should
   * see one ladder and flick along it.
   *
   * ⛔ ROVING TABINDEX (WAI-ARIA radiogroup): exactly ONE pill is tabbable and arrows move within
   * the group. Eight pills each taking a tab stop turns Tab into an eight-press journey past a
   * control most sessions never touch — and this ladder is longer than the one that rule was
   * written for.
   *
   * ⚠ NO WRAP, and it matters MORE here than it did on TF_LADDER: one keypress past the last rung
   * would land on the first — `1d -> 5s`, a ** 17,280x ** jump on a surface where the rung decides
   * what every number on screen means. `tfSwitch.ts` refused wrapping at 1,440x. The traversal
   * rules live in `rungPills.ts` and are pinned against `tfKeyTarget` by an arm that drives BOTH,
   * so this control cannot quietly become a second keyboard.
   */
  import { rungKeyTarget, tabStopIndex } from "./rungPills.ts";
  import { rungLabel, rungAria, rungKey, sameRung, type Rung } from "./rungLadder.ts";

  let {
    options,
    current,
    onchange,
    /** Named for assistive tech: several panes may carry a switcher and "timeframe" alone is ambiguous. */
    label = "chart resolution",
  }: {
    options: readonly Rung[];
    current: Rung;
    onchange: (r: Rung) => void;
    label?: string;
  } = $props();

  /**
   * ⛔⛔ TWO KEYBOARD TRAPS, FOUND BY `codex review` AND DRIVEN BY GK-ad (`bh=49cc51c918`).
   *
   * ① THE GROUP COULD BECOME WHOLLY UNREACHABLE. `tabindex={sameRung(o, current) ? 0 : -1}` gives
   * EVERY pill −1 when `current` is not among `options`, and `rungKeyTarget` returns null for every
   * key in that state. Driven at my hand with real `Rung` fixtures:
   * ```
   *   current IN options ... tabindex [-1, 0,-1]  keys resolve      reachable ✅
   *   current DROPPED ...... tabindex ** [-1,-1,-1] ** keys ALL null  ** UNREACHABLE **
   * ```
   * ⇒ ***THE RADIOGROUP RENDERS, IS VISIBLE, AND IS INOPERABLE BY KEYBOARD.***
   *
   * ⚠ THE CURE IS IN THE TABINDEX, NOT THE TRAVERSAL, AND THAT BOUNDARY IS DELIBERATE.
   * `rungKeyTarget` REFUSES to snap an off-list selection to `options[0]`, that refusal is armed
   * ("a current rung absent from options yields null, never a snap to the first"), and it is pinned
   * against `tfKeyTarget`. Making arrows snap would hide the anomaly and move the owner's chart
   * underneath him — the exact thing that law exists to prevent. **So reachability is restored the
   * way WAI-ARIA already prescribes: in a radiogroup with nothing checked, the FIRST radio is the
   * tab stop.** The anomaly stays visible (no pill is `aria-checked`) and the reader can still get
   * in. Whether ARROWS should re-enter the ladder from an off-list rung is a change to a SHARED
   * ruled contract, so it is a [DESK-Q], not a thing I decide inside one component.
   *
   * ② FOCUS DID NOT FOLLOW THE SELECTION. `onchange` moved the selection and left the ring on a
   * button that becomes `tabindex=-1` — so the next Tab leaves the group from a dead stop, and a
   * screen reader's focus and the group's checked state disagree. Roving tabindex is only half a
   * pattern without the roving FOCUS.
   */
  let groupEl = $state<HTMLDivElement | null>(null);

  /** The rule is DERIVED from the pinned module, never re-decided in markup. */
  let tabStop = $derived(tabStopIndex(options, current));

  function onkeydown(e: KeyboardEvent) {
    const target = rungKeyTarget(options, current, e.key);
    if (target === null) return; // an unhandled key stays unhandled — the control claims nothing
    e.preventDefault();
    onchange(target);
    // Focus follows the selection. The buttons already exist, so this needs no re-render tick.
    const next = options.findIndex((o) => sameRung(o, target));
    if (next >= 0 && groupEl) {
      groupEl.querySelectorAll<HTMLButtonElement>("button.rung")[next]?.focus();
    }
  }
</script>

<!--
  Rendered only when the hub has rungs it has SEEN SERVE. `{#if options.length}` is the surface
  half of `rungOptions()`'s refusal — the logic returns [], and nothing paints.
-->
{#if options.length}
  <div class="rungpills" role="radiogroup" aria-label={label} bind:this={groupEl} {onkeydown}>
    {#each options as o, i (rungKey(o))}
      <button
        type="button"
        role="radio"
        class="rung"
        aria-checked={sameRung(o, current)}
        aria-label={rungAria(o)}
        tabindex={i === tabStop ? 0 : -1}
        onclick={() => onchange(o)}>{rungLabel(o)}</button>
    {/each}
  </div>
{/if}

<style>
  /* Tokens only — law 8. `check:tokens` rejects any colour literal, so both themes come free. */
  .rungpills {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 2px;
    margin-left: 8px;
    /*
      The row's max-content width is 369px with the served ladder. Inside a 350px mobile cell,
      the 8px inset put its right edge at 398px and made the 390px document scroll sideways.
      Keep the inset, but bound the flex line to the containing block and let the pills wrap.
    */
    max-width: calc(100% - 8px);
  }
  .rung {
    font: inherit;
    font-size: calc(10px * var(--ink-scale));
    /* Tabular figures so the pills do not reflow as the owner flicks between 5s and 1440. */
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.04em;
    color: var(--muted);
    background: transparent;
    border: 1px solid var(--line);
    border-radius: 4px;
    padding: 0 5px;
    cursor: pointer;
    /*
      A control the thumb can hit: the PWA is a first-class surface (§19), and 10px text in a
      zero-padding button is a mouse-only target.

      ⛔ 24px IS A FLOOR SET BY WCAG 2.2 SC 2.5.8 (AA), NOT A TASTE — and it is the WHOLE box,
      because `tokens.css` sets `* { box-sizing: border-box }` globally, so the 1px borders come
      out of these numbers rather than adding to them. GK-zz measured 22px here and named
      box-sizing as the bound on his own finding; resolving that bound put the shipped value
      ** 2px UNDER AA **, so this is a defect cured, not a preference applied.
    */
    min-height: 24px;
    min-width: 26px;
  }
  .rung:hover {
    color: var(--ink);
  }
  /*
    The SELECTED rung is marked by weight + ink + a solid edge, NEVER by colour alone — a
    colour-only selected state disappears for a colour-blind reader and in high contrast.
  */
  .rung[aria-checked="true"] {
    color: var(--ink);
    font-weight: 700;
    border-color: var(--ink);
  }
  .rung:focus-visible {
    outline: 2px solid var(--ink);
    outline-offset: 1px;
  }
</style>
